import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  getAllConversations,
  getConversationMessages,
  getChatStats,
  deleteMessage,
  flagMessage,
  sendWarningToUser,
  downloadConversationExport,
  autoScanMessages
} from './adminChatService';
import {
  MessageSquare,
  Search,
  Download,
  Flag,
  Trash2,
  AlertTriangle,
  User,
  Clock,
  Mail,
  FileText,
  X,
  Send,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Eye,
  Shield,
  Loader
} from 'lucide-react';

export default function AdminChatPage() {
  const { isArabic } = useOutletContext();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({});
  const [showUserPanel, setShowUserPanel] = useState(false);

  // Real-time subscription
  const [messageSubscription, setMessageSubscription] = useState(null);

  // Modal states
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Auto-scan state
  const [isScanning, setIsScanning] = useState(false);
  const [lastScanResult, setLastScanResult] = useState(null);
  const [showScanResult, setShowScanResult] = useState(false);

  // Messages container ref for auto-scroll
  const messagesEndRef = useRef(null);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
    loadStats();

    // Poll conversations every 30 seconds (not real-time for performance)
    const interval = setInterval(() => {
      loadConversations();
      loadStats();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Auto-scan messages periodically
  useEffect(() => {
    // Run initial scan after 5 seconds
    const initialScanTimeout = setTimeout(() => {
      runAutoScan();
    }, 5000);

    // Run auto-scan every 60 seconds
    const scanInterval = setInterval(() => {
      runAutoScan();
    }, 60000);

    return () => {
      clearTimeout(initialScanTimeout);
      clearInterval(scanInterval);
    };
  }, []);

  // Reload conversations when search changes (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadConversations();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Load selected conversation messages
  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
      setupRealtimeSubscription(selectedConversation.id);
    }

    return () => {
      // Cleanup subscription when switching conversations
      if (messageSubscription) {
        messageSubscription.unsubscribe();
        setMessageSubscription(null);
      }
    };
  }, [selectedConversation]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  async function loadConversations() {
    try {
      const filters = searchQuery ? { search: searchQuery } : {};
      const data = await getAllConversations(filters);
      setConversations(data.conversations || []);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadMessages(conversationId) {
    try {
      const data = await getConversationMessages(conversationId);
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError(err.message);
    }
  }

  async function loadStats() {
    try {
      const data = await getChatStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }

  // Auto-scan messages for suspicious content
  async function runAutoScan() {
    if (isScanning) return; // Prevent concurrent scans

    try {
      setIsScanning(true);
      const result = await autoScanMessages(24); // Scan last 24 hours
      setLastScanResult(result);

      // Show result notification if new flags were found
      if (result.flagged > 0) {
        setShowScanResult(true);
        // Auto-hide after 10 seconds
        setTimeout(() => setShowScanResult(false), 10000);
        // Reload stats to update flagged count
        loadStats();
      }
    } catch (err) {
      console.error('Error auto-scanning messages:', err);
    } finally {
      setIsScanning(false);
    }
  }

  // Real-time subscription for ONLY the selected conversation
  function setupRealtimeSubscription(conversationId) {
    const channel = supabase
      .channel(`admin-messages-${conversationId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      }, (payload) => {
        setMessages(prev => [...prev, payload.new]);
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      }, (payload) => {
        setMessages(prev => prev.filter(m => m.id !== payload.old.id));
      })
      .subscribe();

    setMessageSubscription(channel);
  }

  async function handleDeleteMessage(message) {
    setSelectedMessage(message);
    setShowDeleteModal(true);
  }

  async function confirmDeleteMessage(reason) {
    if (!selectedMessage || !reason) return;

    try {
      await deleteMessage(selectedMessage.id, reason);
      setMessages(prev => prev.filter(m => m.id !== selectedMessage.id));
      setShowDeleteModal(false);
      setSelectedMessage(null);
      alert(isArabic ? 'تم حذف الرسالة بنجاح' : 'Message deleted successfully');
    } catch (err) {
      alert(isArabic ? 'فشل حذف الرسالة' : 'Failed to delete message: ' + err.message);
    }
  }

  async function handleFlagMessage(message) {
    setSelectedMessage(message);
    setShowFlagModal(true);
  }

  async function confirmFlagMessage(reason, category) {
    if (!selectedMessage || !reason) return;

    try {
      await flagMessage(selectedMessage.id, reason, category);
      setShowFlagModal(false);
      setSelectedMessage(null);
      alert(isArabic ? 'تم الإبلاغ عن الرسالة بنجاح' : 'Message flagged successfully');
    } catch (err) {
      alert(isArabic ? 'فشل الإبلاغ عن الرسالة' : 'Failed to flag message: ' + err.message);
    }
  }

  async function handleSendWarning(userId, userEmail, userType) {
    setShowWarningModal(true);
    // Store user info for warning modal
    setSelectedMessage({ userId, userEmail, userType, conversationId: selectedConversation?.id });
  }

  async function confirmSendWarning(warningData) {
    try {
      console.log('Sending warning with data:', warningData);
      await sendWarningToUser(warningData);
      setShowWarningModal(false);
      setSelectedMessage(null);
      alert(isArabic ? 'تم إرسال التحذير بنجاح' : 'Warning sent successfully');
    } catch (err) {
      console.error('Warning error:', err);
      alert(isArabic ? 'فشل إرسال التحذير' : 'Failed to send warning: ' + err.message);
    }
  }

  async function handleExport(format) {
    if (!selectedConversation) return;

    try {
      await downloadConversationExport(selectedConversation.id, format);
      alert(isArabic ? `تم تصدير المحادثة كـ ${format.toUpperCase()}` : `Conversation exported as ${format.toUpperCase()}`);
    } catch (err) {
      alert(isArabic ? 'فشل التصدير' : 'Export failed: ' + err.message);
    }
  }

  // Main render
  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{ padding: '24px', height: 'calc(100vh - 80px)', overflow: 'hidden' }} dir={isArabic ? 'rtl' : 'ltr'}>
      {/* Header with stats */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: '#333', margin: 0 }}>
            {isArabic ? 'مراقبة المحادثات' : 'Chat Monitoring'}
          </h1>

          {/* Auto-Scan Button */}
          <button
            onClick={runAutoScan}
            disabled={isScanning}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              backgroundColor: isScanning ? '#ccc' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: isScanning ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            onMouseOver={(e) => {
              if (!isScanning) e.currentTarget.style.backgroundColor = '#5568d3';
            }}
            onMouseOut={(e) => {
              if (!isScanning) e.currentTarget.style.backgroundColor = '#667eea';
            }}
          >
            {isScanning ? (
              <>
                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                {isArabic ? 'جاري الفحص...' : 'Scanning...'}
              </>
            ) : (
              <>
                <Shield size={18} />
                {isArabic ? 'فحص الرسائل' : 'Scan Messages'}
              </>
            )}
          </button>
        </div>

        {/* Scan Result Notification */}
        {showScanResult && lastScanResult && (
          <div style={{
            backgroundColor: lastScanResult.flagged > 0 ? '#fff3e0' : '#e8f5e9',
            border: `2px solid ${lastScanResult.flagged > 0 ? '#FF9800' : '#4CAF50'}`,
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {lastScanResult.flagged > 0 ? (
                <AlertTriangle size={20} color="#FF9800" />
              ) : (
                <Shield size={20} color="#4CAF50" />
              )}
              <span style={{ fontSize: 14, fontWeight: 500, color: '#333' }}>
                {isArabic
                  ? `تم فحص ${lastScanResult.scanned} رسالة. تم الإبلاغ عن ${lastScanResult.flagged} رسالة مشبوهة.`
                  : `Scanned ${lastScanResult.scanned} messages. Flagged ${lastScanResult.flagged} suspicious messages.`
                }
              </span>
            </div>
            <button
              onClick={() => setShowScanResult(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                opacity: 0.6
              }}
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Stats cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <StatsCard
            label={isArabic ? 'إجمالي المحادثات' : 'Total Conversations'}
            value={stats.total_conversations || 0}
            icon={MessageSquare}
            color="#667eea"
          />
          <StatsCard
            label={isArabic ? 'إجمالي الرسائل' : 'Total Messages'}
            value={stats.total_messages || 0}
            icon={Mail}
            color="#4CAF50"
          />
          <StatsCard
            label={isArabic ? 'الرسائل المبلغ عنها' : 'Flagged Messages'}
            value={stats.flagged_count || 0}
            icon={Flag}
            color="#f44336"
          />
          <StatsCard
            label={isArabic ? 'التحذيرات المرسلة' : 'Warnings Sent'}
            value={stats.warnings_count || 0}
            icon={AlertTriangle}
            color="#FF9800"
          />
        </div>
      </div>

      {/* Three-column grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: showUserPanel ? '320px 1fr 300px' : '320px 1fr',
        gap: 16,
        height: 'calc(100% - 180px)',
        overflow: 'hidden'
      }}>
        {/* Left: Conversations list */}
        <ConversationsList
          conversations={conversations}
          selectedConversation={selectedConversation}
          onSelectConversation={setSelectedConversation}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          loading={loading}
          isArabic={isArabic}
        />

        {/* Center: Messages */}
        <MessagesPanel
          messages={messages}
          selectedConversation={selectedConversation}
          onDeleteMessage={handleDeleteMessage}
          onFlagMessage={handleFlagMessage}
          onExport={handleExport}
          onShowUserPanel={() => setShowUserPanel(!showUserPanel)}
          showUserPanel={showUserPanel}
          messagesEndRef={messagesEndRef}
          isArabic={isArabic}
        />

        {/* Right: User details (optional) */}
        {showUserPanel && selectedConversation && (
          <UserDetailsPanel
            conversation={selectedConversation}
            onSendWarning={handleSendWarning}
            onClose={() => setShowUserPanel(false)}
            isArabic={isArabic}
          />
        )}
      </div>

      {/* Modals */}
      {showDeleteModal && (
        <DeleteMessageModal
          message={selectedMessage}
          onConfirm={confirmDeleteMessage}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedMessage(null);
          }}
          isArabic={isArabic}
        />
      )}

      {showFlagModal && (
        <FlagMessageModal
          message={selectedMessage}
          onConfirm={confirmFlagMessage}
          onCancel={() => {
            setShowFlagModal(false);
            setSelectedMessage(null);
          }}
          isArabic={isArabic}
        />
      )}

      {showWarningModal && (
        <WarningModal
          userInfo={selectedMessage}
          onConfirm={confirmSendWarning}
          onCancel={() => {
            setShowWarningModal(false);
            setSelectedMessage(null);
          }}
          isArabic={isArabic}
        />
      )}
    </div>
    </>
  );
}

// ============= SUB-COMPONENTS =============

function StatsCard({ label, value, icon: Icon, color = '#667eea' }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      padding: '20px',
      border: '1px solid #e0e0e0',
      display: 'flex',
      alignItems: 'center',
      gap: 15
    }}>
      <div style={{
        background: `${color}15`,
        borderRadius: 10,
        padding: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Icon size={24} color={color} />
      </div>
      <div>
        <p style={{ fontSize: 13, color: '#666', margin: 0, marginBottom: 4 }}>{label}</p>
        <p style={{ fontSize: 26, fontWeight: 700, color: '#333', margin: 0 }}>{value}</p>
      </div>
    </div>
  );
}

function ConversationsList({ conversations, selectedConversation, onSelectConversation, searchQuery, onSearchChange, loading, isArabic }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      border: '1px solid #e0e0e0',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100%'
    }}>
      {/* Search header */}
      <div style={{ padding: 16, borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ position: 'relative' }}>
          <Search size={18} color="#999" style={{ position: 'absolute', left: isArabic ? 'auto' : 12, right: isArabic ? 12 : 'auto', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder={isArabic ? 'ابحث عن طريق الاسم أو البريد الإلكتروني...' : 'Search by name or email...'}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              paddingLeft: isArabic ? 12 : 40,
              paddingRight: isArabic ? 40 : 12,
              border: '1px solid #ddd',
              borderRadius: 8,
              fontSize: 14,
              outline: 'none'
            }}
          />
        </div>
      </div>

      {/* Conversations list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
            {isArabic ? 'جاري التحميل...' : 'Loading...'}
          </div>
        ) : conversations.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
            <MessageSquare size={48} color="#ddd" style={{ marginBottom: 10 }} />
            <p>{isArabic ? 'لا توجد محادثات' : 'No conversations found'}</p>
          </div>
        ) : (
          conversations.map(conv => (
            <ConversationCard
              key={conv.id}
              conversation={conv}
              isSelected={selectedConversation?.id === conv.id}
              onClick={() => onSelectConversation(conv)}
              isArabic={isArabic}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ConversationCard({ conversation, isSelected, onClick, isArabic }) {
  const { user1_details, user2_details, last_message, created_at } = conversation;

  return (
    <div
      onClick={onClick}
      style={{
        padding: 16,
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer',
        background: isSelected ? '#f5f7ff' : 'white',
        transition: 'background 0.2s'
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = isSelected ? '#f5f7ff' : '#fafafa'}
      onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? '#f5f7ff' : 'white'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#333', margin: 0 }}>
              {user1_details.full_name} & {user2_details.full_name}
            </p>
            {/* Flag indicator for conversations with flagged messages */}
            {conversation.has_flagged_messages && (
              <div style={{
                background: '#ffebee',
                padding: '2px 6px',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 3
              }}>
                <Flag size={12} color="#f44336" />
                <span style={{ fontSize: 10, fontWeight: 700, color: '#f44336' }}>
                  {isArabic ? 'مبلغ' : 'FLAGGED'}
                </span>
              </div>
            )}
          </div>
          <p style={{ fontSize: 12, color: '#999', margin: 0, marginTop: 4 }}>
            {user1_details.user_type} ↔ {user2_details.user_type}
          </p>
        </div>
        <Clock size={14} color="#999" />
      </div>

      {last_message && (
        <p style={{
          fontSize: 13,
          color: '#666',
          margin: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {last_message.content || (isArabic ? '[مرفق]' : '[Attachment]')}
        </p>
      )}

      <p style={{ fontSize: 11, color: '#aaa', margin: 0, marginTop: 6 }}>
        {new Date(created_at).toLocaleDateString()}
      </p>
    </div>
  );
}

function MessagesPanel({ messages, selectedConversation, onDeleteMessage, onFlagMessage, onExport, onShowUserPanel, showUserPanel, messagesEndRef, isArabic }) {
  if (!selectedConversation) {
    return (
      <div style={{
        background: 'white',
        borderRadius: 12,
        border: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
      }}>
        <MessageSquare size={64} color="#ddd" />
        <p style={{ color: '#999', marginTop: 16 }}>
          {isArabic ? 'اختر محادثة لعرض الرسائل' : 'Select a conversation to view messages'}
        </p>
      </div>
    );
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      border: '1px solid #e0e0e0',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100%'
    }}>
      {/* Header */}
      <div style={{ padding: 16, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, marginBottom: 4, color: '#333' }}>
            {selectedConversation.user1_details.full_name} & {selectedConversation.user2_details.full_name}
          </h3>
          <p style={{ fontSize: 12, color: '#999', margin: 0 }}>
            {messages.length} {isArabic ? 'رسالة' : 'messages'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => onExport('pdf')}
            style={{
              padding: '8px 16px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <Download size={16} />
            PDF
          </button>
          <button
            onClick={() => onExport('csv')}
            style={{
              padding: '8px 16px',
              background: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <Download size={16} />
            CSV
          </button>
          <button
            onClick={onShowUserPanel}
            style={{
              padding: '8px 12px',
              background: showUserPanel ? '#667eea' : '#f5f5f5',
              color: showUserPanel ? 'white' : '#666',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <User size={16} />
            {isArabic ? 'التفاصيل' : 'Details'}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20, background: '#fafafa' }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            {isArabic ? 'لا توجد رسائل في هذه المحادثة' : 'No messages in this conversation'}
          </div>
        ) : (
          <>
            {messages.map(msg => (
              <MessageBubble
                key={msg.id}
                message={msg}
                onDelete={onDeleteMessage}
                onFlag={onFlagMessage}
                isArabic={isArabic}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ message, onDelete, onFlag, isArabic }) {
  const [showActions, setShowActions] = useState(false);

  return (
    <div
      style={{ marginBottom: 16, position: 'relative' }}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {/* Sender info */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <User size={16} color="#667eea" />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#667eea' }}>
              {message.sender_details?.full_name || 'Unknown'}
            </span>
            <span style={{ fontSize: 11, color: '#999' }}>
              {new Date(message.created_at).toLocaleString()}
            </span>
            {/* Flagged indicator */}
            {message.is_flagged && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 8px',
                background: '#ffebee',
                borderRadius: 12,
                fontSize: 11,
                fontWeight: 600,
                color: '#f44336'
              }}
              title={message.flag_info?.flag_reason || 'Flagged'}
              >
                <Flag size={12} />
                {isArabic ? 'مبلغ عنها' : 'FLAGGED'}
              </div>
            )}
          </div>

          <div style={{
            background: message.is_flagged ? '#fff5f5' : 'white',
            padding: '12px 16px',
            borderRadius: 12,
            border: message.is_flagged ? '2px solid #f44336' : '1px solid #e0e0e0',
            position: 'relative',
            boxShadow: message.is_flagged ? '0 2px 8px rgba(244, 67, 54, 0.15)' : 'none'
          }}>
            <p style={{ margin: 0, fontSize: 14, color: '#333', lineHeight: 1.6 }}>
              {message.content || (isArabic ? '[محتوى فارغ]' : '[Empty content]')}
            </p>

            {message.attachment_url && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#667eea' }}>
                📎 {message.attachment_name || 'Attachment'}
              </div>
            )}

            {/* Flag reason */}
            {message.is_flagged && message.flag_info && (
              <div style={{
                marginTop: 10,
                padding: '8px 12px',
                background: '#ffebee',
                borderRadius: 8,
                border: '1px solid #f44336',
                fontSize: 12
              }}>
                <div style={{ fontWeight: 600, color: '#f44336', marginBottom: 4 }}>
                  🚩 {isArabic ? 'سبب الإبلاغ:' : 'Flag Reason:'}
                </div>
                <div style={{ color: '#666' }}>
                  {message.flag_info.flag_reason}
                </div>
                <div style={{ marginTop: 4, fontSize: 11, color: '#999' }}>
                  {isArabic ? 'الفئة:' : 'Category:'} {message.flag_info.flag_category} |
                  {isArabic ? ' الحالة:' : ' Status:'} {message.flag_info.status}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions - Show on hover */}
        {showActions && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => onFlag(message)}
              style={{
                padding: 8,
                background: '#fff3e0',
                border: '1px solid #FFB74D',
                borderRadius: 6,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={isArabic ? 'إبلاغ' : 'Flag'}
            >
              <Flag size={16} color="#FF9800" />
            </button>
            <button
              onClick={() => onDelete(message)}
              style={{
                padding: 8,
                background: '#ffebee',
                border: '1px solid #EF5350',
                borderRadius: 6,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={isArabic ? 'حذف' : 'Delete'}
            >
              <Trash2 size={16} color="#f44336" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function UserDetailsPanel({ conversation, onSendWarning, onClose, isArabic }) {
  const { user1_details, user2_details } = conversation;

  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      border: '1px solid #e0e0e0',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100%'
    }}>
      {/* Header */}
      <div style={{ padding: 16, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: '#333' }}>
          {isArabic ? 'تفاصيل المستخدمين' : 'User Details'}
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4
          }}
        >
          <X size={20} color="#666" />
        </button>
      </div>

      {/* User cards */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <UserCard user={user1_details} onSendWarning={onSendWarning} isArabic={isArabic} />
        <div style={{ height: 16 }} />
        <UserCard user={user2_details} onSendWarning={onSendWarning} isArabic={isArabic} />
      </div>
    </div>
  );
}

function UserCard({ user, onSendWarning, isArabic }) {
  return (
    <div style={{
      background: '#fafafa',
      borderRadius: 10,
      padding: 16,
      border: '1px solid #e0e0e0'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: '#667eea',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 20,
          fontWeight: 600
        }}>
          {user.full_name?.charAt(0) || '?'}
        </div>
        <div>
          <p style={{ fontSize: 15, fontWeight: 600, margin: 0, color: '#333' }}>
            {user.full_name || 'Unknown'}
          </p>
          <p style={{ fontSize: 12, color: '#999', margin: 0 }}>
            {user.user_type}
          </p>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <p style={{ fontSize: 12, color: '#666', margin: 0, marginBottom: 4 }}>
          {isArabic ? 'البريد الإلكتروني' : 'Email'}
        </p>
        <p style={{ fontSize: 13, color: '#333', margin: 0 }}>
          {user.email || 'N/A'}
        </p>
      </div>

      <button
        onClick={() => onSendWarning(user.id, user.email, user.user_type)}
        style={{
          width: '100%',
          padding: '10px 16px',
          background: '#FF9800',
          color: 'white',
          border: 'none',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8
        }}
      >
        <AlertTriangle size={16} />
        {isArabic ? 'إرسال تحذير' : 'Send Warning'}
      </button>
    </div>
  );
}

// ============= MODALS =============

function DeleteMessageModal({ message, onConfirm, onCancel, isArabic }) {
  const [reason, setReason] = useState('');

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: 12,
        padding: 24,
        maxWidth: 500,
        width: '90%'
      }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0, marginBottom: 16, color: '#333' }}>
          {isArabic ? 'حذف رسالة' : 'Delete Message'}
        </h3>

        <p style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
          {isArabic ? 'الرجاء تقديم سبب لحذف هذه الرسالة. سيتم تسجيل هذا الإجراء.' : 'Please provide a reason for deleting this message. This action will be logged.'}
        </p>

        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={isArabic ? 'سبب الحذف (10 أحرف على الأقل)' : 'Reason for deletion (min 10 characters)'}
          style={{
            width: '100%',
            padding: 12,
            border: '1px solid #ddd',
            borderRadius: 8,
            fontSize: 14,
            minHeight: 100,
            resize: 'vertical',
            fontFamily: 'inherit'
          }}
        />

        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button
            onClick={() => reason.length >= 10 && onConfirm(reason)}
            disabled={reason.length < 10}
            style={{
              flex: 1,
              padding: '12px 24px',
              background: reason.length >= 10 ? '#f44336' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: reason.length >= 10 ? 'pointer' : 'not-allowed'
            }}
          >
            {isArabic ? 'حذف' : 'Delete'}
          </button>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '12px 24px',
              background: '#f5f5f5',
              color: '#333',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {isArabic ? 'إلغاء' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FlagMessageModal({ message, onConfirm, onCancel, isArabic }) {
  const [reason, setReason] = useState('');
  const [category, setCategory] = useState('inappropriate');

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: 12,
        padding: 24,
        maxWidth: 500,
        width: '90%'
      }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0, marginBottom: 16, color: '#333' }}>
          {isArabic ? 'الإبلاغ عن رسالة' : 'Flag Message'}
        </h3>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 8 }}>
            {isArabic ? 'الفئة' : 'Category'}
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{
              width: '100%',
              padding: 10,
              border: '1px solid #ddd',
              borderRadius: 8,
              fontSize: 14
            }}
          >
            <option value="inappropriate">{isArabic ? 'غير لائق' : 'Inappropriate'}</option>
            <option value="spam">{isArabic ? 'بريد مزعج' : 'Spam'}</option>
            <option value="harassment">{isArabic ? 'تحرش' : 'Harassment'}</option>
            <option value="other">{isArabic ? 'آخر' : 'Other'}</option>
          </select>
        </div>

        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={isArabic ? 'سبب الإبلاغ (10 أحرف على الأقل)' : 'Reason for flagging (min 10 characters)'}
          style={{
            width: '100%',
            padding: 12,
            border: '1px solid #ddd',
            borderRadius: 8,
            fontSize: 14,
            minHeight: 100,
            resize: 'vertical',
            fontFamily: 'inherit'
          }}
        />

        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button
            onClick={() => reason.length >= 10 && onConfirm(reason, category)}
            disabled={reason.length < 10}
            style={{
              flex: 1,
              padding: '12px 24px',
              background: reason.length >= 10 ? '#FF9800' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: reason.length >= 10 ? 'pointer' : 'not-allowed'
            }}
          >
            {isArabic ? 'إبلاغ' : 'Flag'}
          </button>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '12px 24px',
              background: '#f5f5f5',
              color: '#333',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {isArabic ? 'إلغاء' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

function WarningModal({ userInfo, onConfirm, onCancel, isArabic }) {
  const [warningType, setWarningType] = useState('inappropriate_content');
  const [warningMessage, setWarningMessage] = useState('');
  const [severity, setSeverity] = useState('medium');

  const handleSubmit = () => {
    if (warningMessage.length < 20) return;

    onConfirm({
      user_id: userInfo.userId,
      user_email: userInfo.userEmail,
      user_type: userInfo.userType,
      warning_type: warningType,
      warning_message: warningMessage,
      severity,
      related_conversation_id: userInfo.conversationId
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: 12,
        padding: 24,
        maxWidth: 500,
        width: '90%'
      }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0, marginBottom: 16, color: '#333' }}>
          {isArabic ? 'إرسال تحذير للمستخدم' : 'Send Warning to User'}
        </h3>

        <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
          {isArabic ? `إرسال تحذير إلى: ${userInfo?.userEmail}` : `Sending warning to: ${userInfo?.userEmail}`}
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 8 }}>
            {isArabic ? 'نوع التحذير' : 'Warning Type'}
          </label>
          <select
            value={warningType}
            onChange={(e) => setWarningType(e.target.value)}
            style={{
              width: '100%',
              padding: 10,
              border: '1px solid #ddd',
              borderRadius: 8,
              fontSize: 14
            }}
          >
            <option value="inappropriate_content">{isArabic ? 'محتوى غير لائق' : 'Inappropriate Content'}</option>
            <option value="spam">{isArabic ? 'بريد مزعج' : 'Spam'}</option>
            <option value="harassment">{isArabic ? 'تحرش' : 'Harassment'}</option>
            <option value="policy_violation">{isArabic ? 'انتهاك السياسة' : 'Policy Violation'}</option>
            <option value="other">{isArabic ? 'آخر' : 'Other'}</option>
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 8 }}>
            {isArabic ? 'الخطورة' : 'Severity'}
          </label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            style={{
              width: '100%',
              padding: 10,
              border: '1px solid #ddd',
              borderRadius: 8,
              fontSize: 14
            }}
          >
            <option value="low">{isArabic ? 'منخفض' : 'Low'}</option>
            <option value="medium">{isArabic ? 'متوسط' : 'Medium'}</option>
            <option value="high">{isArabic ? 'عالي' : 'High'}</option>
            <option value="critical">{isArabic ? 'حرج' : 'Critical'}</option>
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 8 }}>
            {isArabic ? 'رسالة التحذير' : 'Warning Message'}
          </label>
          <textarea
            value={warningMessage}
            onChange={(e) => setWarningMessage(e.target.value)}
            placeholder={isArabic ? 'رسالة التحذير (20 حرفًا على الأقل)' : 'Warning message (min 20 characters)'}
            style={{
              width: '100%',
              padding: 12,
              border: '1px solid #ddd',
              borderRadius: 8,
              fontSize: 14,
              minHeight: 120,
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
          />
          <p style={{ fontSize: 11, color: '#999', margin: '4px 0 0 0' }}>
            {warningMessage.length} / 20 {isArabic ? 'حرف' : 'characters'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button
            onClick={handleSubmit}
            disabled={warningMessage.length < 20}
            style={{
              flex: 1,
              padding: '12px 24px',
              background: warningMessage.length >= 20 ? '#FF9800' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: warningMessage.length >= 20 ? 'pointer' : 'not-allowed'
            }}
          >
            <Send size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {isArabic ? 'إرسال تحذير' : 'Send Warning'}
          </button>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '12px 24px',
              background: '#f5f5f5',
              color: '#333',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {isArabic ? 'إلغاء' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}
